import type {
  AlvoSimulacao,
  CategoriaProjecaoEntrada,
  ClassificacaoEscolhida,
  CustoPadraoEntrada,
  CategoriaProjecaoGravada,
  CriarProjecaoBody,
  MoedaOpcao,
  ParametroCategoria,
  ProjecaoEscopo,
  SimularProjecaoBody,
} from "@/modules/financeiro-obras";
import {
  FATOR_FORA_DA_FAIXA,
  FATOR_OBRIGATORIO,
  MATERIAIS_TODOS_OU_NENHUM,
  VALOR_MAXIMO,
  VALOR_OBRIGATORIO,
} from "./projecao-formulario.constants";

export type CategoriaForm = {
  valor: number | null;
  moedaId: string | null;
  distribuicaoId: string | null;
  curvaId: string | null;
};

export type ProjecaoForm = {
  servicos: CategoriaForm;
  materiais: CategoriaForm;
};

export type ErrosCategoria = Partial<Record<keyof CategoriaForm, string>>;
export interface ErrosProjecao {
  servicos: ErrosCategoria;
  materiais: ErrosCategoria;
}

export type ExtrasProjecao = {
  usarCustoPadrao: boolean;
  aplicarFator: boolean;
  fatorIdade: number | null;
  classificacao: ClassificacaoEscolhida | null;
};

export const SEM_EXTRAS: ExtrasProjecao = {
  usarCustoPadrao: false,
  aplicarFator: false,
  fatorIdade: null,
  classificacao: null,
};

const custoPadraoDe = (e: ExtrasProjecao): CustoPadraoEntrada =>
  e.usarCustoPadrao
    ? { usarCustoPadrao: true, ...(e.aplicarFator ? { fatorIdade: e.fatorIdade } : {}) }
    : {};

export function erroFator({ usarCustoPadrao, aplicarFator, fatorIdade }: ExtrasProjecao) {
  if (!usarCustoPadrao || !aplicarFator) return undefined;
  if (fatorIdade === null) return FATOR_OBRIGATORIO;
  return fatorIdade < 0 || fatorIdade > 100 ? FATOR_FORA_DA_FAIXA : undefined;
}

const VALOR_MAXIMO_PROJECAO = 100_000_000_000;

export const categoriaVazia = (moedaId: string | null): CategoriaForm => ({
  valor: null,
  moedaId,
  distribuicaoId: null,
  curvaId: null,
});

export const materiaisPreenchido = ({ valor, distribuicaoId, curvaId }: CategoriaForm) =>
  valor !== null || distribuicaoId !== null || curvaId !== null;

function gravavel({
  valor,
  moedaId,
  distribuicaoId,
  curvaId,
}: CategoriaForm): CategoriaProjecaoGravada | null {
  if (valor === null || valor <= 0 || !moedaId || !distribuicaoId || !curvaId) return null;
  return { valor, moedaId, distribuicaoId, curvaId };
}

const categoriaCompleta = (c: CategoriaForm) => gravavel(c) !== null;

function erroValor(valor: number | null): string | undefined {
  if (valor === null) return undefined;
  if (valor <= 0) return VALOR_OBRIGATORIO;
  return valor > VALOR_MAXIMO_PROJECAO ? VALOR_MAXIMO : undefined;
}

export function validar(form: ProjecaoForm): ErrosProjecao {
  const materiais: ErrosCategoria = {};
  const m = form.materiais;
  const valor = erroValor(m.valor);
  if (valor) materiais.valor = valor;
  if (materiaisPreenchido(m)) {
    if (m.valor === null) materiais.valor = MATERIAIS_TODOS_OU_NENHUM;
    if (!m.distribuicaoId) materiais.distribuicaoId = MATERIAIS_TODOS_OU_NENHUM;
    if (!m.curvaId) materiais.curvaId = MATERIAIS_TODOS_OU_NENHUM;
  }
  const servicos: ErrosCategoria = {};
  const servicosValor = erroValor(form.servicos.valor);
  if (servicosValor) servicos.valor = servicosValor;
  return { servicos, materiais };
}

export function podeSalvar(form: ProjecaoForm): boolean {
  const erros = validar(form);
  const semErro =
    [...Object.values(erros.servicos), ...Object.values(erros.materiais)].length === 0;
  const materiaisOk = !materiaisPreenchido(form.materiais) || categoriaCompleta(form.materiais);
  return semErro && categoriaCompleta(form.servicos) && materiaisOk;
}

const entrada = (c: CategoriaForm): CategoriaProjecaoEntrada => ({
  valor: c.valor,
  moedaId: c.moedaId,
  distribuicaoId: c.distribuicaoId,
  curvaId: c.curvaId,
});

const algumCampo = (c: CategoriaForm) =>
  c.valor !== null || c.distribuicaoId !== null || c.curvaId !== null;

export function montarSimulacao(
  alvo: AlvoSimulacao,
  form: ProjecaoForm,
  extras: ExtrasProjecao = SEM_EXTRAS,
): SimularProjecaoBody | null {
  if (!algumCampo(form.servicos) && !materiaisPreenchido(form.materiais)) return null;
  return {
    ...alvo,
    servicos: entrada(form.servicos),
    materiais: materiaisPreenchido(form.materiais) ? entrada(form.materiais) : null,
    ...custoPadraoDe(extras),
  };
}

export function montarGravacao(
  form: ProjecaoForm,
  extras: ExtrasProjecao = SEM_EXTRAS,
): CriarProjecaoBody | null {
  const servicos = gravavel(form.servicos);
  if (!servicos) return null;
  const adicionais = {
    ...custoPadraoDe(extras),
    ...(extras.classificacao ? { classificacao: extras.classificacao } : {}),
  };
  if (!materiaisPreenchido(form.materiais)) return { servicos, materiais: null, ...adicionais };
  const materiais = gravavel(form.materiais);
  return materiais ? { servicos, materiais, ...adicionais } : null;
}

const daVigente = (p: ParametroCategoria, moedas: MoedaOpcao[]): CategoriaForm => ({
  valor: p.valor,
  moedaId: moedas.find((m) => m.codigo === p.moeda)?.id ?? "",
  distribuicaoId: p.distribuicao?.id ?? null,
  curvaId: p.curva?.id ?? null,
});

export const formularioDaVigente = (
  projecao: Pick<ProjecaoEscopo, "servicos" | "materiais">,
  moedas: MoedaOpcao[],
  moedaPadrao: string | null,
): ProjecaoForm => ({
  servicos: daVigente(projecao.servicos, moedas),
  materiais: projecao.materiais
    ? daVigente(projecao.materiais, moedas)
    : categoriaVazia(moedaPadrao),
});
